/**
 * `validate/ip-validate` — IPv4 / IPv6 / CIDR syntactic validator
 * with classification.
 *
 * Sister of `network/ip-subnet`: that tool computes network /
 * broadcast / host range from an already-legal CIDR; THIS one
 * answers "is this string a valid IP, and what kind?" plus a
 * private/loopback/link-local/multicast/reserved/documentation
 * classification.
 *
 * IPv6 is parsed by hand (no external dep). We accept:
 *   - normal `xxxx:xxxx:...` form, 8 groups
 *   - any single `::` zero-run shortening
 *   - the IPv4-tail form `::ffff:192.0.2.1` (last group expanded
 *     to two 16-bit groups)
 *
 * We do NOT accept zone identifiers (`fe80::1%eth0`) — they're a
 * networking-stack concept rather than an addressing one and most
 * tooling we'd interop with strips them.
 */

export type IpKind = 'ipv4' | 'ipv6' | 'cidr-v4' | 'cidr-v6' | 'invalid';

export interface IpClass {
  isPrivate: boolean;
  isLoopback: boolean;
  isLinkLocal: boolean;
  isMulticast: boolean;
  isReserved: boolean;
  isDocumentation: boolean;
  /** "10.0.0.0/8", "fc00::/7" — the matching range, if any. */
  matchedRange?: string;
}

/* ------------------------------------------------------------------ */
/* IPv4                                                                */
/* ------------------------------------------------------------------ */

const IPV4_OCTET = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

export function isIpv4(s: string): boolean {
  if (typeof s !== 'string') return false;
  const parts = s.split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => IPV4_OCTET.test(p));
}

function ipv4ToInt(s: string): number {
  const [a, b, c, d] = s.split('.').map(Number);
  return ((a << 24) | (b << 16) | (c << 8) | d) >>> 0;
}

interface Ipv4Range {
  cidr: string;
  base: number;
  mask: number;
}

function makeRange(cidr: string): Ipv4Range {
  const [ip, prefStr] = cidr.split('/');
  const pref = Number(prefStr);
  const mask = pref === 0 ? 0 : (0xffffffff << (32 - pref)) >>> 0;
  return { cidr, base: ipv4ToInt(ip) & mask, mask };
}

function rangeContainsV4(r: Ipv4Range, ipInt: number): boolean {
  return (ipInt & r.mask) === r.base;
}

const V4_PRIVATE = ['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16'].map(makeRange);
const V4_LOOPBACK = ['127.0.0.0/8'].map(makeRange);
const V4_LINK_LOCAL = ['169.254.0.0/16'].map(makeRange);
const V4_MULTICAST = ['224.0.0.0/4'].map(makeRange);
const V4_DOCUMENTATION = [
  '192.0.2.0/24',
  '198.51.100.0/24',
  '203.0.113.0/24',
].map(makeRange);
const V4_RESERVED = ['0.0.0.0/8', '240.0.0.0/4'].map(makeRange);

function classifyV4(ip: string): IpClass {
  const n = ipv4ToInt(ip);
  let matchedRange: string | undefined;
  const inAny = (ranges: Ipv4Range[]): boolean => {
    for (const r of ranges) {
      if (rangeContainsV4(r, n)) {
        matchedRange = r.cidr;
        return true;
      }
    }
    return false;
  };

  // Order matters: a 0.0.0.0 lookup should report "reserved" before
  // private — our return rule is "first true wins" via `inAny`'s
  // mutation of `matchedRange`.
  const isReserved = inAny(V4_RESERVED);
  const isLoopback = !isReserved && inAny(V4_LOOPBACK);
  const isLinkLocal = !isReserved && !isLoopback && inAny(V4_LINK_LOCAL);
  const isMulticast = !isReserved && !isLoopback && !isLinkLocal && inAny(V4_MULTICAST);
  const isDocumentation =
    !isReserved && !isLoopback && !isLinkLocal && !isMulticast &&
    inAny(V4_DOCUMENTATION);
  const isPrivate =
    !isReserved && !isLoopback && !isLinkLocal && !isMulticast && !isDocumentation &&
    inAny(V4_PRIVATE);

  return {
    isPrivate,
    isLoopback,
    isLinkLocal,
    isMulticast,
    isReserved,
    isDocumentation,
    matchedRange,
  };
}

/* ------------------------------------------------------------------ */
/* IPv6                                                                */
/* ------------------------------------------------------------------ */

/**
 * Expand an IPv6 string (with optional `::` and optional IPv4 tail)
 * to a length-8 `Uint16Array`. Returns null if the string isn't a
 * legal IPv6 representation.
 */
function parseIpv6(s: string): Uint16Array | null {
  if (typeof s !== 'string') return null;
  if (s.length === 0) return null;
  // Reject inputs with multiple `::`
  const dblColons = s.match(/::/g);
  if (dblColons && dblColons.length > 1) return null;

  // Split into "before" and "after" of `::` (if present).
  let head: string[];
  let tail: string[];
  const idx = s.indexOf('::');
  if (idx === -1) {
    head = s.split(':');
    tail = [];
  } else {
    const left = s.slice(0, idx);
    const right = s.slice(idx + 2);
    head = left ? left.split(':') : [];
    tail = right ? right.split(':') : [];
  }

  // The last group (in `tail` if present, otherwise in `head`) may be
  // an IPv4 dotted quad — expand to two 16-bit groups.
  const lastList = tail.length > 0 ? tail : head;
  if (lastList.length > 0 && lastList[lastList.length - 1].includes('.')) {
    const v4 = lastList.pop()!;
    if (!isIpv4(v4)) return null;
    const [a, b, c, d] = v4.split('.').map(Number);
    lastList.push(((a << 8) | b).toString(16));
    lastList.push(((c << 8) | d).toString(16));
  }

  // Each remaining group must be 1–4 hex digits.
  const re = /^[0-9a-fA-F]{1,4}$/;
  for (const g of [...head, ...tail]) {
    if (!re.test(g)) return null;
  }

  const total = head.length + tail.length;
  if (idx === -1) {
    if (total !== 8) return null;
  } else {
    if (total > 7) return null; // `::` must replace at least one zero group
  }

  const out = new Uint16Array(8);
  for (let i = 0; i < head.length; i++) out[i] = parseInt(head[i], 16);
  for (let i = 0; i < tail.length; i++) out[8 - tail.length + i] = parseInt(tail[i], 16);
  return out;
}

export function isIpv6(s: string): boolean {
  return parseIpv6(s) !== null;
}

interface Ipv6Range {
  cidr: string;
  /** First N bits to compare. */
  prefix: number;
  /** Network address as a Uint16Array (8 groups). */
  base: Uint16Array;
}

function makeV6Range(cidr: string): Ipv6Range {
  const [addr, prefStr] = cidr.split('/');
  const pref = Number(prefStr);
  const groups = parseIpv6(addr)!;
  return { cidr, prefix: pref, base: groups };
}

function rangeContainsV6(r: Ipv6Range, addr: Uint16Array): boolean {
  let bitsLeft = r.prefix;
  for (let i = 0; i < 8 && bitsLeft > 0; i++) {
    const groupBits = Math.min(16, bitsLeft);
    const mask = groupBits === 16 ? 0xffff : (0xffff << (16 - groupBits)) & 0xffff;
    if ((addr[i] & mask) !== (r.base[i] & mask)) return false;
    bitsLeft -= groupBits;
  }
  return true;
}

const V6_LOOPBACK = ['::1/128'].map(makeV6Range);
const V6_UNSPECIFIED = ['::/128'].map(makeV6Range);
const V6_LINK_LOCAL = ['fe80::/10'].map(makeV6Range);
const V6_UNIQUE_LOCAL = ['fc00::/7'].map(makeV6Range);
const V6_MULTICAST = ['ff00::/8'].map(makeV6Range);
const V6_DOCUMENTATION = ['2001:db8::/32'].map(makeV6Range);
// "Reserved" in v6 covers everything not yet allocated by IANA;
// we tag the well-known reserved blocks rather than the long tail.
const V6_RESERVED = ['::/128', '::ffff:0:0/96'].map(makeV6Range);

function classifyV6(addr: Uint16Array): IpClass {
  let matchedRange: string | undefined;
  const inAny = (ranges: Ipv6Range[]): boolean => {
    for (const r of ranges) {
      if (rangeContainsV6(r, addr)) {
        matchedRange = r.cidr;
        return true;
      }
    }
    return false;
  };

  const isLoopback = inAny(V6_LOOPBACK);
  const isReserved = !isLoopback && (inAny(V6_RESERVED) || inAny(V6_UNSPECIFIED));
  const isLinkLocal = !isLoopback && !isReserved && inAny(V6_LINK_LOCAL);
  const isMulticast = !isLoopback && !isReserved && !isLinkLocal && inAny(V6_MULTICAST);
  const isDocumentation =
    !isLoopback && !isReserved && !isLinkLocal && !isMulticast &&
    inAny(V6_DOCUMENTATION);
  const isPrivate =
    !isLoopback && !isReserved && !isLinkLocal && !isMulticast && !isDocumentation &&
    inAny(V6_UNIQUE_LOCAL);

  return {
    isPrivate,
    isLoopback,
    isLinkLocal,
    isMulticast,
    isReserved,
    isDocumentation,
    matchedRange,
  };
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export function detectIp(s: string): IpKind {
  if (typeof s !== 'string') return 'invalid';
  const trimmed = s.trim();
  if (!trimmed) return 'invalid';

  if (trimmed.includes('/')) {
    const [addr, prefStr] = trimmed.split('/');
    if (prefStr === undefined || prefStr === '') return 'invalid';
    if (!/^\d+$/.test(prefStr)) return 'invalid';
    const pref = Number(prefStr);
    if (isIpv4(addr) && pref >= 0 && pref <= 32) return 'cidr-v4';
    if (isIpv6(addr) && pref >= 0 && pref <= 128) return 'cidr-v6';
    return 'invalid';
  }

  if (isIpv4(trimmed)) return 'ipv4';
  if (isIpv6(trimmed)) return 'ipv6';
  return 'invalid';
}

export function classifyIp(s: string): IpClass | null {
  const kind = detectIp(s);
  const trimmed = s.trim();
  if (kind === 'ipv4') return classifyV4(trimmed);
  if (kind === 'cidr-v4') return classifyV4(trimmed.split('/')[0]);
  if (kind === 'ipv6') {
    const groups = parseIpv6(trimmed);
    return groups ? classifyV6(groups) : null;
  }
  if (kind === 'cidr-v6') {
    const groups = parseIpv6(trimmed.split('/')[0]);
    return groups ? classifyV6(groups) : null;
  }
  return null;
}
